import { Component, type ErrorInfo, type ReactNode } from "react";
import { Icon } from "./Icon";

interface Props {
  children: ReactNode;
  /** Optional reset handler (e.g. navigate home). Defaults to a full reload. */
  onReset?: () => void;
}
interface State {
  error: Error | null;
}

/**
 * Catches render-time errors anywhere below it and shows a branded fallback
 * instead of a blank white screen. A production SaaS must never hard-blank on
 * a single component throwing (this is what made /stripe-link appear empty).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface in the console for diagnosis; wire to telemetry later if desired.
    console.error("render_error", error, info.componentStack);
  }

  handleReset = (): void => {
    this.setState({ error: null });
    if (this.props.onReset) this.props.onReset();
    else if (typeof window !== "undefined") window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="fd-errorboundary">
        <div className="fd-errorboundary__card">
          <span className="fd-errorboundary__icon">
            <Icon name="alert" size={26} />
          </span>
          <h1 className="fd-h3">Something went wrong on this screen</h1>
          <p className="fd-body">
            The page hit an unexpected error and couldn’t finish loading. Your data is safe — try
            reloading.
          </p>
          <pre className="fd-errorboundary__detail">{this.state.error.message}</pre>
          <button type="button" className="fd-btn fd-btn--gold" onClick={this.handleReset}>
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
